// Tutor CoursesPage — 4 stat cards, a client-side-filtering tab strip, and
// the tutor's own courses as cards (server-derived courseStatus per course).
import { useEffect, useMemo, useState } from "react";
import { getJson } from "../../hooks/useApi.js";
import { useCourses } from "../../contexts/CoursesContext.js";
import StatCard from "../../components/shared/StatCard.js";
import Badge from "../../components/shared/Badge.js";
import TutorCourseCard from "../../components/tutor/TutorCourseCard.js";
import type { CourseStats, CourseStatsByCourseId, CourseStatus } from "@dm-lms/shared/types";

type TabKey = "all" | CourseStatus;

const TABS: Array<{ key: TabKey; label: string; tone: "gray" | "blue" | "green" }> = [
  { key: "all",          label: "All",          tone: "gray" },
  { key: "in_progress",  label: "In progress",  tone: "blue" },
  { key: "not_started",  label: "Not started",  tone: "gray" },
  { key: "completed",    label: "Completed",    tone: "green" },
];

export default function CoursesPage() {
  const { courses: myCourses, isLoading, error } = useCourses();

  const [statsByCourse, setStatsByCourse] = useState<Map<string, CourseStats>>(new Map());
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (myCourses.length === 0) {
      setStatsByCourse(new Map());
      return;
    }
    let cancelled = false;
    setStatsLoading(true);

    const courseIds = myCourses.map((course) => course.id).join(",");
    void getJson<CourseStatsByCourseId>(`/api/courses/stats?courseIds=${courseIds}`).then((res) => {
      if (cancelled) return;
      const map = new Map<string, CourseStats>();
      if (res.ok) {
        for (const [id, stats] of Object.entries(res.data)) map.set(id, stats);
      }
      setStatsByCourse(map);
      setStatsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [myCourses]);

  const counts = useMemo(() => {
    let inProgress = 0;
    let notStarted = 0;
    let completed = 0;
    for (const course of myCourses) {
      switch (statsByCourse.get(course.id)?.courseStatus) {
        case "in_progress":
          inProgress += 1;
          break;
        case "not_started":
          notStarted += 1;
          break;
        case "completed":
          completed += 1;
          break;
      }
    }
    return { all: myCourses.length, in_progress: inProgress, not_started: notStarted, completed };
  }, [myCourses, statsByCourse]);

  const [activeTab, setActiveTab] = useState<TabKey>("all");

  // Tab switch is a pure client-side filter over already-fetched data — no
  // refetch of courses or stats happens here.
  const filteredCourses = useMemo(() => {
    if (activeTab === "all") return myCourses;
    return myCourses.filter((course) => statsByCourse.get(course.id)?.courseStatus === activeTab);
  }, [myCourses, statsByCourse, activeTab]);

  const activeTabLabel = TABS.find((tab) => tab.key === activeTab)!.label;
  const showContent = !isLoading && !error && myCourses.length > 0 && !statsLoading;

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">My Courses</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading courses…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && myCourses.length === 0 && (
        <p className="text-dm-slate text-sm">No courses assigned yet. Contact admin.</p>
      )}

      {!isLoading && !error && myCourses.length > 0 && statsLoading && (
        <p className="text-dm-slate text-sm">Loading course stats…</p>
      )}

      {showContent && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total courses" value={counts.all} />
            <StatCard label="In progress" value={counts.in_progress} />
            <StatCard label="Not started" value={counts.not_started} />
            <StatCard label="Completed" value={counts.completed} />
          </div>

          <div className="mb-6 inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-[0.5px] border-gray-300 bg-white text-dm-navy"
                      : "text-dm-slate hover:text-dm-navy"
                  }`}
                >
                  {tab.label}
                  <Badge tone={tab.tone}>{counts[tab.key]}</Badge>
                </button>
              );
            })}
          </div>

          {filteredCourses.length === 0 ? (
            <p className="text-dm-slate text-sm">No {activeTabLabel.toLowerCase()} courses.</p>
          ) : (
            <div className="space-y-4">
              {filteredCourses.map((course) => (
                <TutorCourseCard key={course.id} course={course} stats={statsByCourse.get(course.id)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
